import type { OMSConfig, StrategyEvaluator, SignalResult, StrategyContext } from '../types';
import { getConnectingDotsRows, getSortKey } from './shared';
import { cellDataKeys, ensureFlatOpts } from '../../analysis/trend-evaluator';
import { extractSnapshotOptions } from '../../../lib/snapshot-search';

export const evaluateConnectingDotsItmTop5: StrategyEvaluator = (data: any, currentUnixTime: number, config: OMSConfig, context: StrategyContext): SignalResult | null => {
    if (!data) return null;

    const { symbol, expiry, activeRange, gap, settingsMode } = context;
    const spot = data.spotPrice || 0;
    if (spot <= 0) return null;
    
    const atmStrike = Math.round(spot / gap) * gap;

    const { itmRows, otmRows } = getConnectingDotsRows(data, symbol, expiry, atmStrike, activeRange, gap);
    itmRows.sort((a: any, b: any) => getSortKey(b, settingsMode) - getSortKey(a, settingsMode));
    otmRows.sort((a: any, b: any) => getSortKey(b, settingsMode) - getSortKey(a, settingsMode));

    const top5Itm = itmRows.slice(0, 5);
    const top5Otm = otmRows.slice(0, 5);
    const combinedRows = [...top5Itm, ...top5Otm];

    let bullishVol = 0, bearishVol = 0;
    
    const valKey = cellDataKeys[settingsMode] || 'volume'; // Fallback to volume to match UI

    for (const r of combinedRows) {
        const val = (r as any)[valKey] || 0;
        const isCE = r.type === 'CE';
        const isItm = isCE ? r.strike <= atmStrike : r.strike >= atmStrike;
        const isOtm = isCE ? r.strike > atmStrike : r.strike < atmStrike;
        
        if ((isCE && isItm) || (!isCE && isOtm)) {
            bullishVol += val;
        } else if ((!isCE && isItm) || (isCE && isOtm)) {
            bearishVol += val;
        }
    }

    let status = 'Sideways';

    if (bullishVol > bearishVol) status = 'BULL';
    else if (bearishVol > bullishVol) status = 'BEAR';

    if (status === 'Sideways') return null;

    // Use top 1 ITM strike for entry
    const entryStrike = top5Itm[0]?.strike || atmStrike;
    let entryPrice = 0;
    
    const liveRawOptsArr = context.chain || extractSnapshotOptions(data, symbol, expiry) || [];
    const liveFlatOpts = ensureFlatOpts(liveRawOptsArr);

    const optionType = status === 'BULL' ? 'CE' : 'PE';
    const atmRow = liveFlatOpts.find(r => r.strike === entryStrike && r.type === optionType);
    if (atmRow) {
        entryPrice = atmRow.ltp || atmRow.close || atmRow.price || 0;
    }

    if (entryPrice > 0) {
        return {
            trade_type: status as any,
            strike: entryStrike,
            option_type: optionType,
            entry_price: entryPrice,
            remarks: `Top 5 ITM ${context.settingsMode.includes('OI') ? 'OI' : 'Vol'} (${status === 'BULL' ? 'CE>PE' : 'PE>CE'})`
        };
    }

    return null;
};
